import { WeComIcon } from '@/components/icons'
import { helpTopicUrl } from '@/lib/help-links'
import type { BlockConfig, BlockMeta } from '@/blocks/types'
import { AuthMode, IntegrationType } from '@/blocks/types'

export const WeComBlock: BlockConfig = {
  type: 'wecom',
  name: 'WeCom',
  description: 'Read WeCom documents and send application messages',
  longDescription:
    'Connect a WeCom self-built app to read authorized WeDoc documents and send text messages to app users. Enable document API access for this application in the enterprise administration console.',
  authMode: AuthMode.OAuth,
  category: 'tools',
  integrationType: IntegrationType.Productivity,
  bgColor: 'var(--integration-wecom)',
  icon: WeComIcon,
  docsLink: helpTopicUrl('integrations'),
  subBlocks: [
    {
      id: 'operation',
      value: () => 'read_document',
      title: 'Operation',
      type: 'dropdown',
      options: [
        {
          id: 'read_document',
          label: 'Read WeCom document',
        },
        {
          id: 'send_message',
          label: 'Send WeCom message',
        },
      ],
    },
    {
      id: 'credential',
      title: 'WeCom account',
      type: 'oauth-input',
      credentialKind: 'service-account',
      serviceId: 'wecom',
      canonicalParamId: 'oauthCredential',
      mode: 'basic',
      required: true,
    },
    {
      id: 'manualCredential',
      title: 'WeCom account',
      type: 'short-input',
      canonicalParamId: 'oauthCredential',
      mode: 'advanced',
      placeholder: 'Enter credential ID',
      required: true,
    },
    {
      id: 'documentId',
      title: 'Document ID',
      type: 'short-input',
      placeholder: 'Enter the document ID',
      required: true,
      condition: {
        field: 'operation',
        value: 'read_document',
      },
    },
    {
      id: 'agentId',
      title: 'AgentId',
      type: 'short-input',
      placeholder: '1000002',
      required: true,
      condition: {
        field: 'operation',
        value: 'send_message',
      },
    },
    {
      id: 'userIds',
      title: 'Recipient user IDs',
      type: 'short-input',
      placeholder: 'zhangsan|lisi',
      required: true,
      condition: {
        field: 'operation',
        value: 'send_message',
      },
    },
    {
      id: 'text',
      title: 'Message text',
      type: 'long-input',
      placeholder: 'Enter the message text',
      required: true,
      condition: {
        field: 'operation',
        value: 'send_message',
      },
    },
  ],
  tools: {
    access: ['wecom_read_document', 'wecom_send_message'],
    config: { tool: (p) => `wecom_${p.operation || 'read_document'}` },
  },
  inputs: {
    documentId: {
      type: 'string',
      description: 'Document ID',
    },
    agentId: {
      type: 'string',
      description: 'AgentId',
    },
    userIds: {
      type: 'string',
      description: 'Recipient user IDs',
    },
    text: {
      type: 'string',
      description: 'Message text',
    },
    oauthCredential: {
      type: 'string',
      description: 'Connected app credential ID',
    },
  },
  outputs: {
    content: {
      type: 'string',
      description: 'Document or table text',
    },
    messageId: {
      type: 'string',
      description: 'Provider message ID',
    },
    version: {
      type: 'number',
      description: 'Document version',
    },
  },
}
export const WeComBlockMeta = {
  tags: ['messaging', 'knowledge-base'],
  templates: [],
} satisfies BlockMeta
